import {
  CircleStackIcon,
  DocumentIcon,
  RocketLaunchIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { type JSX } from "react";
import { FilterLogic } from "@via/pages/sample-trackers/samples/types/filters.ts";

interface FilterTagsRowProps {
  label: string;
  icon: "database" | "run" | "document";
  filters: Array<{ displayText: string }>;
  logic: FilterLogic;
  onRemove: (index: number) => void;
  onLogicChange: (logic: FilterLogic) => void;
  onEdit?: (index: number) => void;
}

export default function FilterTagsRow({
  label,
  icon,
  filters,
  logic,
  onRemove,
  onLogicChange,
  onEdit,
}: FilterTagsRowProps): JSX.Element {
  if (filters.length === 0) {
    return <></>;
  }

  const IconComponent =
    icon === "database"
      ? CircleStackIcon
      : icon === "run"
        ? RunIcon
        : DocumentIcon;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-medium text-gray-700">{label}:</span>
      {filters.length > 1 && (
        <select
          value={logic}
          onChange={e => onLogicChange(e.target.value as FilterLogic)}
          className="text-sm border border-gray-300 rounded-md px-3 py-1.5 min-w-[70px] bg-white text-gray-700 focus:outline-none appearance-none cursor-pointer">
          <option value="AND">AND</option>
          <option value="OR">OR</option>
        </select>
      )}
      {filters.map((filter, index) => (
        <div key={index} className="flex items-center gap-2">
          {index > 0 && (
            <span className="text-sm font-medium text-gray-500">{logic}</span>
          )}
          <div
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 text-gray-700 rounded-full text-sm ${
              onEdit ? "cursor-pointer hover:bg-gray-300" : ""
            } transition-colors`}
            onClick={onEdit ? () => onEdit(index) : undefined}
            role={onEdit ? "button" : undefined}
            tabIndex={onEdit ? 0 : undefined}
            onKeyDown={
              onEdit
                ? e => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onEdit(index);
                    }
                  }
                : undefined
            }>
            <IconComponent className="h-4 w-4 text-gray-600" />
            <span>{filter.displayText}</span>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                onRemove(index);
              }}
              className="hover:bg-gray-400 rounded-full p-0.5 transition-colors">
              <XMarkIcon className="h-3.5 w-3.5 text-gray-600" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function RunIcon({ className }: { className?: string }): JSX.Element {
  return <RocketLaunchIcon className={className} />;
}
